/**
 * <s-accordion> — Animates a native <details> open and closed.
 *
 * Markup:
 *   <s-accordion><details><summary>…</summary><div data-accordion-content>…</div></details></s-accordion>
 *
 * Opening grows the content from 0 to its full height, pushing the page down smoothly,
 * and scrolls the window along so the newly opened links come into view.
 * Closing shrinks it back before the <details> actually closes.
 * Reduced-motion visitors get an instant toggle.
 */
class SAccordion extends HTMLElement {
  #controller = null;
  #animation = null;

  connectedCallback() {
    const details = this.querySelector('details');
    const summary = details?.querySelector('summary');
    const content = details?.querySelector('[data-accordion-content]');
    if (!details || !summary || !content) return;
    this.#controller = new AbortController();

    summary.addEventListener(
      'click',
      (event) => {
        if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
        event.preventDefault();
        const closing = details.open && !details.hasAttribute('data-closing');
        if (closing) this.#close(details, content);
        else this.#open(details, content);
      },
      { signal: this.#controller.signal }
    );
  }

  disconnectedCallback() {
    this.#controller?.abort();
    this.#animation?.cancel();
  }

  #duration() {
    const value = getComputedStyle(this).getPropertyValue('--open-duration').trim();
    return parseFloat(value) || 420;
  }

  #easing() {
    return getComputedStyle(this).getPropertyValue('--ease-open').trim() || 'cubic-bezier(0.22, 1, 0.36, 1)';
  }

  #open(details, content) {
    const from = details.open ? content.getBoundingClientRect().height : 0;
    details.removeAttribute('data-closing');
    details.open = true;
    const to = content.scrollHeight;
    const duration = this.#duration();
    this.#animation?.cancel();
    content.style.overflow = 'hidden';
    this.#animation = content.animate(
      [{ height: `${from}px`, opacity: from ? 1 : 0 }, { height: `${to}px`, opacity: 1 }],
      { duration, easing: this.#easing() }
    );
    this.#animation.onfinish = () => {
      content.style.overflow = '';
      this.#animation = null;
    };

    // Bring the screen down with the opening: if the opened links end below the fold, scroll along with them.
    const bottom = details.getBoundingClientRect().top + (details.getBoundingClientRect().height - from) + to;
    const overflow = bottom - window.innerHeight + 24;
    if (overflow > 0) window.scrollBy({ top: overflow, behavior: 'smooth' });
  }

  #close(details, content) {
    const from = content.getBoundingClientRect().height;
    details.setAttribute('data-closing', '');
    this.#animation?.cancel();
    content.style.overflow = 'hidden';
    this.#animation = content.animate(
      [{ height: `${from}px`, opacity: 1 }, { height: '0px', opacity: 0 }],
      { duration: Math.round(this.#duration() * 0.75), easing: this.#easing() }
    );
    this.#animation.onfinish = () => {
      details.open = false;
      details.removeAttribute('data-closing');
      content.style.overflow = '';
      this.#animation = null;
    };
  }
}

if (!customElements.get('s-accordion')) customElements.define('s-accordion', SAccordion);
